import React from "react";
import { Link } from "react-router-dom";
import { Mail, Instagram, Download, FileText, Camera, Trophy, Users, BookOpen, Sparkles, ExternalLink, Copy, Check, Gift } from "lucide-react";
import { useLang } from "../lib/i18n";
import ContestBadge from "../components/ContestBadge";

const EMAIL = "bonjour@legangdesemotions.fr";
const INSTA = "https://www.instagram.com/legangdesemotions/";
const SITE  = "https://legangdesemotions.app";

const MASCOTS = [
  { id: "anger",      en: "Grr the dragon",      fr: "Grr le dragon",            theme_fr: "la colère",      theme_en: "anger" },
  { id: "anxiety",    en: "Bandit the raccoon",  fr: "Bandit le raton laveur",   theme_fr: "l'anxiété",      theme_en: "anxiety" },
  { id: "confidence", en: "Max the lion",        fr: "Max le lion",              theme_fr: "la confiance",   theme_en: "confidence" },
  { id: "fear",       en: "Hoohoo the owl",      fr: "Hoohoo le hibou",          theme_fr: "la peur",        theme_en: "fear" },
  { id: "sadness",    en: "Cloudy the sheep",    fr: "Cloudy le mouton",         theme_fr: "la tristesse",   theme_en: "sadness" },
  { id: "joy",        en: "Sparky the fox",      fr: "Sparky le renard",         theme_fr: "la joie",        theme_en: "joy" },
];

function CopyButton({ value }) {
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };
  return (
    <button
      onClick={copy}
      data-testid={`press-copy-${value.slice(0, 20)}`}
      className="inline-flex items-center gap-1 text-[11px] text-[#8A8D91] hover:text-[#3D405B] transition-colors"
      title="Copier"
    >
      {copied ? <Check className="w-3 h-3 text-[#73A38B]" /> : <Copy className="w-3 h-3" />}
    </button>
  );
}

function Figure({ icon: Icon, big, label, accent = "#F4A261" }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E8E6E1] p-5 flex items-center gap-4" data-testid="press-figure">
      <div className="w-11 h-11 rounded-xl grid place-items-center shrink-0" style={{ background: `${accent}22` }}>
        <Icon className="w-5 h-5" style={{ color: accent }} />
      </div>
      <div>
        <div className="font-heading text-2xl text-[#3D405B] leading-none">{big}</div>
        <div className="text-xs text-[#8A8D91] mt-1">{label}</div>
      </div>
    </div>
  );
}

export default function Presse() {
  const { lang } = useLang();
  const isFr = lang === "fr";

  React.useEffect(() => {
    const prev = document.title;
    document.title = isFr
      ? "Presse — Le Gang Des Émotions®"
      : "Press — Le Gang Des Émotions®";
    return () => { document.title = prev; };
  }, [isFr]);

  const t = {
    eyebrow: isFr ? "Espace presse" : "Press room",
    title:   isFr ? "Kit média" : "Media kit",
    subtitle: isFr
      ? "Tout ce qu'il faut pour parler de Le Gang Des Émotions® — photos, chiffres, bio, visuels prêts à publier."
      : "Everything you need to cover Le Gang Des Émotions® — photos, numbers, bio, publish-ready assets.",
    bioTitle: isFr ? "L'autrice" : "The author",
    bioBody: isFr
      ? "Ex-Global Program Manager en Propriété Intellectuelle devenue autrice, Aurélia P. a troqué ses tableurs pour une page blanche afin de créer des outils qui soutiennent les familles. Maman d'un petit garçon, elle est à l'origine de Le Gang Des Émotions® : des livres et une application qui aident les enfants à apprivoiser leurs émotions, et les parents à les accompagner avec bienveillance."
      : "A former Global Program Manager in Intellectual Property turned author, Aurélia P. traded spreadsheets for a blank page to create tools that support families. A mum to a young boy, she is the creator of Le Gang Des Émotions®: books and an app that help children tame their emotions, and parents guide them with kindness.",
    figuresTitle: isFr ? "Chiffres-clés" : "Key figures",
    rankingLabel: isFr ? "TOP 100 / 909 apps · Emergent France 2026" : "TOP 100 / 909 apps · Emergent France 2026",
    booksLabel:   isFr ? "livres jeunesse de la collection" : "children's books in the collection",
    mascotsLabel: isFr ? "mascottes émotionnelles"           : "emotional mascots",
    featuresLabel: isFr ? "modules dans l'app (chat, défouloir, humeur, quiz, victoires, diplômes)" : "modules in the app (chat, worry box, mood tracker, quiz, wins, diplomas)",
    bilingual:    isFr ? "100 % bilingue FR / EN"            : "100% bilingual FR / EN",
    freeBig:      isFr ? "Gratuite"                          : "Free",
    freeLabel:    isFr ? "Entièrement gratuite pour tous les parents" : "Entirely free for every parent",

    cast: isFr ? "Le casting (6 mascottes)" : "The cast (6 mascots)",
    assets: isFr ? "Visuels à télécharger" : "Downloadable assets",
    assetsNote: isFr
      ? "Clic droit → Enregistrer. Haute résolution. Libres d'usage éditorial avec crédit « Aurélia P. / Le Gang Des Émotions® »."
      : "Right-click → Save. High-resolution. Free for editorial use with credit \"Aurélia P. / Le Gang Des Émotions®\".",
    coverCredit: (name) => isFr ? `Couverture — ${name}` : `Cover — ${name}`,

    contactTitle: isFr ? "Contact presse" : "Press contact",
    contactIntro: isFr
      ? "Interview, extrait, visuel supplémentaire, visuel sur-mesure ? Un mot et je réponds dans la journée."
      : "Interview, excerpt, extra visual, custom asset? One word and I'll reply the same day.",
    emailBtn: isFr ? "Écrire par email" : "Write by email",
    pressRelease: isFr ? "Communiqué de presse (texte prêt à copier)" : "Press release (copy-ready text)",
    pressBody: isFr ? PRESS_FR() : PRESS_EN(),

    pitch: isFr ? "Phrase d'accroche" : "Pitch line",
    pitchQuote: isFr
      ? "« Une appli compagnon qui aide les parents à accueillir les tempêtes émotionnelles de leurs enfants — née de 6 albums illustrés et saluée par le concours Emergent (TOP 100 sur 909 apps françaises). »"
      : "\"A companion app that helps parents hold their children's emotional storms — born from 6 illustrated books and recognized by the Emergent contest (TOP 100 of 909 French apps).\"",
  };

  return (
    <div className="space-y-10" data-testid="presse-page">
      {/* HERO */}
      <header className="rounded-3xl border border-[#E8E6E1] bg-gradient-to-br from-[#FFF4E0] via-[#FDFBF7] to-[#FFEADF] p-8 md:p-12">
        <div className="text-xs uppercase tracking-widest text-[#E07A5F] font-semibold inline-flex items-center gap-2">
          <FileText className="w-4 h-4" /> {t.eyebrow}
        </div>
        <h1 className="font-heading text-4xl md:text-5xl text-[#3D405B] mt-3">{t.title}</h1>
        <p className="text-[#3D405B]/80 text-lg mt-3 max-w-2xl leading-relaxed">{t.subtitle}</p>
        <div className="mt-6 flex flex-wrap gap-3 items-center">
          <ContestBadge />
          <a
            href={`mailto:${EMAIL}?subject=${encodeURIComponent(isFr ? "Interview Le Gang Des Émotions" : "Interview Le Gang Des Émotions")}`}
            data-testid="press-email-cta"
            className="inline-flex items-center gap-2 bg-[#3D405B] text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-[#2f3147] transition-colors"
          >
            <Mail className="w-4 h-4" /> {t.emailBtn}
          </a>
        </div>
      </header>

      {/* BIO */}
      <section className="grid md:grid-cols-[260px,1fr,320px] gap-6 items-start">
        <figure className="rounded-3xl overflow-hidden border border-[#E8E6E1] bg-white" data-testid="press-portrait">
          <img
            src="/aurelia.jpg"
            alt={isFr ? "Aurélia P. avec les 6 mascottes du Gang Des Émotions" : "Aurélia P. with the 6 mascots of Le Gang Des Émotions"}
            className="w-full aspect-square object-cover"
            loading="lazy"
          />
          <figcaption className="text-[11px] text-[#8A8D91] text-center px-3 py-2 border-t border-[#E8E6E1]">
            {isFr ? "Aurélia P. — autrice et créatrice" : "Aurélia P. — author & creator"}
          </figcaption>
        </figure>
        <article className="rounded-3xl bg-white border border-[#E8E6E1] p-6 md:p-8">
          <h2 className="font-heading text-2xl text-[#3D405B] inline-flex items-center gap-2">
            <Users className="w-5 h-5 text-[#E07A5F]" /> {t.bioTitle}
          </h2>
          <p className="text-[#3D405B]/85 text-base leading-relaxed mt-3">{t.bioBody}</p>
        </article>
        <blockquote className="rounded-3xl p-5 md:p-6 text-[#3D405B] text-sm italic leading-relaxed border-2 border-dashed border-[#F4A261] bg-[#FFF9EF]" data-testid="press-pitch">
          <div className="text-[10px] uppercase tracking-widest text-[#E07A5F] font-bold not-italic mb-2 inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> {t.pitch}
          </div>
          {t.pitchQuote}
          <div className="mt-2 flex justify-end">
            <CopyButton value={t.pitchQuote.replace(/^«\s|«|»/g, "").replace(/^"|"$/g, "").trim()} />
          </div>
        </blockquote>
      </section>

      {/* FIGURES */}
      <section>
        <h2 className="font-heading text-2xl text-[#3D405B] inline-flex items-center gap-2 mb-4">
          <Trophy className="w-5 h-5 text-[#F4A261]" /> {t.figuresTitle}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Figure icon={Trophy}   big="100/909"       label={t.rankingLabel}   accent="#F4A261" />
          <Figure icon={BookOpen}  big="6"             label={t.booksLabel}     accent="#E07A5F" />
          <Figure icon={Users}     big="6"             label={t.mascotsLabel}   accent="#B08BBB" />
          <Figure icon={Sparkles}  big="7"             label={t.featuresLabel}  accent="#73A38B" />
          <Figure icon={FileText}  big="FR / EN"       label={t.bilingual}      accent="#7C9DBA" />
          <Figure icon={Gift}      big={t.freeBig}     label={t.freeLabel}      accent="#E6B26D" />
        </div>
      </section>

      {/* CAST */}
      <section>
        <h2 className="font-heading text-2xl text-[#3D405B] inline-flex items-center gap-2 mb-4">
          <Camera className="w-5 h-5 text-[#B08BBB]" /> {t.cast}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="press-mascots-grid">
          {MASCOTS.map((m) => (
            <figure key={m.id} className="bg-white rounded-2xl border border-[#E8E6E1] p-3 flex flex-col items-center text-center">
              <img src={`/covers/${m.id}.jpg`} alt="" className="w-full aspect-square object-cover rounded-xl" loading="lazy" />
              <figcaption className="mt-2 text-[13px] font-heading text-[#3D405B] leading-tight">
                {isFr ? m.fr : m.en}
              </figcaption>
              <div className="text-[11px] text-[#8A8D91] mt-0.5">
                {isFr ? `· ${m.theme_fr}` : `· ${m.theme_en}`}
              </div>
            </figure>
          ))}
        </div>
      </section>

      {/* DOWNLOADABLE ASSETS */}
      <section>
        <h2 className="font-heading text-2xl text-[#3D405B] inline-flex items-center gap-2 mb-2">
          <Download className="w-5 h-5 text-[#73A38B]" /> {t.assets}
        </h2>
        <p className="text-sm text-[#8A8D91] mb-4">{t.assetsNote}</p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {MASCOTS.map((m) => (
            <a
              key={m.id}
              href={`/covers/${m.id}.jpg`}
              download={`le-gang-couverture-${m.id}.jpg`}
              data-testid={`press-download-${m.id}`}
              className="rounded-2xl bg-white border border-[#E8E6E1] p-3 flex items-center gap-3 hover:border-[#F4A261] transition-colors"
            >
              <img src={`/covers/${m.id}.jpg`} alt="" className="w-14 h-14 rounded-lg object-cover" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#3D405B] truncate">{t.coverCredit(isFr ? m.fr : m.en)}</div>
                <div className="text-[11px] text-[#8A8D91] inline-flex items-center gap-1">JPG <Download className="w-3 h-3" /></div>
              </div>
            </a>
          ))}
          <a
            href="/og-image.jpg"
            download="le-gang-og-image.jpg"
            data-testid="press-download-og"
            className="rounded-2xl bg-white border border-[#E8E6E1] p-3 flex items-center gap-3 hover:border-[#F4A261] transition-colors"
          >
            <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-[#F4A261] to-[#E07A5F] grid place-items-center shrink-0">
              <Camera className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-[#3D405B]">{isFr ? "Visuel OG 1200×630" : "OG image 1200×630"}</div>
              <div className="text-[11px] text-[#8A8D91] inline-flex items-center gap-1">JPG <Download className="w-3 h-3" /></div>
            </div>
          </a>
        </div>
      </section>

      {/* PRESS RELEASE */}
      <section className="rounded-3xl bg-white border border-[#E8E6E1] p-6 md:p-8">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <h2 className="font-heading text-2xl text-[#3D405B] inline-flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#3D405B]" /> {t.pressRelease}
          </h2>
          <CopyButton value={t.pressBody} />
        </div>
        <pre
          data-testid="press-release"
          className="whitespace-pre-wrap break-words text-sm text-[#3D405B]/85 leading-relaxed font-sans bg-[#FDFBF7] rounded-xl p-4 border border-[#E8E6E1] max-h-[480px] overflow-auto"
        >
{t.pressBody}
        </pre>
      </section>

      {/* CONTACT */}
      <section className="rounded-3xl p-6 md:p-8 border-2 border-[#F4A261] bg-gradient-to-r from-[#FFF4E0] to-[#FFEADF]">
        <h2 className="font-heading text-2xl text-[#3D405B] inline-flex items-center gap-2">
          <Mail className="w-5 h-5 text-[#E07A5F]" /> {t.contactTitle}
        </h2>
        <p className="text-[#3D405B]/85 mt-2 max-w-2xl">{t.contactIntro}</p>
        <div className="mt-4 flex flex-wrap gap-3">
          <a href={`mailto:${EMAIL}`}
             className="inline-flex items-center gap-2 bg-[#3D405B] text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-[#2f3147] transition-colors">
            <Mail className="w-4 h-4" /> {EMAIL}
          </a>
          <a href={INSTA} target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-2 bg-gradient-to-r from-[#E07A5F] to-[#F4A261] text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity">
            <Instagram className="w-4 h-4" /> @legangdesemotions
          </a>
          <a href={SITE} className="inline-flex items-center gap-2 bg-white border-2 border-[#3D405B] text-[#3D405B] rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-[#3D405B] hover:text-white transition-colors">
            <ExternalLink className="w-4 h-4" /> legangdesemotions.app
          </a>
        </div>
      </section>

      <div className="text-center">
        <Link to="/" className="text-sm text-[#8A8D91] hover:text-[#3D405B]">
          ← {isFr ? "Retour à l'accueil" : "Back to home"}
        </Link>
      </div>
    </div>
  );
}

function PRESS_FR() {
  return `COMMUNIQUÉ DE PRESSE — septembre 2026

Le Gang Des Émotions® : l'appli compagnon qui aide les parents à apprivoiser les tempêtes émotionnelles de leurs enfants, saluée par le concours Emergent France.

PARIS, septembre 2026 — Le Gang Des Émotions®, l'application compagnon de la collection jeunesse éponyme écrite et illustrée par Aurélia P., vient d'être classée dans le TOP 100 des 909 applications françaises soumises au concours Emergent France 2026.

Pensée comme un véritable havre pour les parents, l'application prolonge l'univers des 6 albums jeunesse (Grr le dragon, Bandit le raton laveur, Max le lion, Hoohoo le hibou, Cloudy le mouton et Sparky le renard) à travers 7 modules concrets :
• un compagnon « Le Gang » disponible 24/7 pour accueillir les crises du soir,
• un défouloir où déposer ses petits cailloux,
• un suivi d'humeur avec analyses hebdomadaires,
• 30+ conseils tirés directement des livres,
• un quiz de parentalité bienveillante,
• un mur des victoires,
• des diplômes officiels à télécharger et afficher sur le frigo.

« Je voulais que mes livres continuent à aider les familles au-delà de la lecture du soir. L'app c'est ça : un prolongement doux, sans écran pour l'enfant, qui soutient les parents quand les tempêtes émotionnelles arrivent », explique Aurélia P.

Entièrement gratuite, 100 % bilingue FR / EN, l'app est accessible dès aujourd'hui sur legangdesemotions.app.

À propos : Ex-Global Program Manager en Propriété Intellectuelle devenue autrice, Aurélia P. a troqué ses tableurs pour une page blanche afin de créer des outils qui soutiennent les familles. Maman d'un petit garçon, elle est à l'origine de Le Gang Des Émotions® : des livres et une application qui aident les enfants à apprivoiser leurs émotions, et les parents à les accompagner avec bienveillance.

Contact presse : bonjour@legangdesemotions.fr
Instagram : @legangdesemotions
Site : legangdesemotions.app`;
}

function PRESS_EN() {
  return `PRESS RELEASE — September 2026

Le Gang Des Émotions®: the companion app that helps parents hold their children's emotional storms, recognized by the Emergent France contest.

PARIS, September 2026 — Le Gang Des Émotions®, the companion app for the eponymous children's book collection written and illustrated by Aurélia P., has been ranked in the TOP 100 of 909 French apps submitted to the Emergent France 2026 contest.

Designed as a true haven for parents, the app extends the universe of the 6 children's books (Grr the dragon, Bandit the raccoon, Max the lion, Hoohoo the owl, Cloudy the sheep, and Sparky the fox) through 7 concrete modules:
• a 24/7 "Le Gang" companion to welcome evening meltdowns,
• a worry box to put down the little pebbles,
• a mood tracker with weekly insights,
• 30+ parenting tips pulled directly from the books,
• a gentle-parenting quiz,
• a wins wall,
• official diplomas to download and stick on the fridge.

"I wanted my books to keep helping families beyond bedtime reading. The app is exactly that: a soft extension, screen-free for the child, that supports parents when emotional storms arrive," says Aurélia P.

Entirely free, 100% bilingual FR / EN, the app is available today at legangdesemotions.app.

About: A former Global Program Manager in Intellectual Property turned author, Aurélia P. traded spreadsheets for a blank page to create tools that support families. A mum to a young boy, she is the creator of Le Gang Des Émotions®: books and an app that help children tame their emotions, and parents guide them with kindness.

Press contact: bonjour@legangdesemotions.fr
Instagram: @legangdesemotions
Site: legangdesemotions.app`;
}
